import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PAD, Z_BLOCK, Z_SPOT, Z_TIP, ANIM_CSS } from "./CoachMarkTour";

// ─── SCROLL-TRIGGERED COACH MARK ────────────────────────────────────────────
// A one-shot version of CoachMarkTour's spotlight+tooltip pair, for content
// that lives further down a long page than a tab-open tour would ever reach
// without the user already having scrolled past it — currently the Habit
// Stats sub-page's Premium-only Target Completion / Overall Completion
// sections (ProfileTab.jsx).
//
// Triggered by the target scrolling into view (IntersectionObserver), not by
// a tab opening. Tap anywhere to dismiss, same as CoachMarkTour's plain
// (non-interactive) steps — there's nothing here to interact with, just an
// explanation. The caller owns the "seen it" flag: this component only calls
// onDismiss when the user taps it away, and stops rendering once its parent
// stops passing gating conditions that keep it mounted.
const REVEAL_THRESHOLD = 0.6; // fraction of the target that must be on screen
const POLL_MS = 100;
const TIP_MARGIN = 14;
const NAV_SAFE_PX = 118; // bottom nav height + breathing room

const sameRect = (a, b) =>
  Math.abs(a.top - b.top) < 0.5 && Math.abs(a.left - b.left) < 0.5 &&
  Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5;

function ScrollCoachMark({ targetRef, title, body, onDismiss }) {
  const [revealed, setRevealed] = useState(false);
  const [rect, setRect] = useState(null);
  const rectRef = useRef(null);
  useEffect(() => { rectRef.current = rect; }, [rect]);

  // Watches until the target scrolls into view, then stops — nothing left to
  // watch for once it's been revealed once.
  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setRevealed(true); },
      { threshold: REVEAL_THRESHOLD }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [targetRef]);

  // Once revealed, keep the spotlight glued to the target — the user can
  // keep scrolling after the panel appears, and the highlight should track it.
  useEffect(() => {
    if (!revealed) return;
    const measure = () => {
      const el = targetRef.current;
      if (!el) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      setRect(prev => (prev && sameRect(prev, r) ? prev : r));
    };
    measure();
    const poll = setInterval(measure, POLL_MS);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      clearInterval(poll);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [revealed, targetRef]);

  // Tap anywhere dismisses, captured before it reaches its target so it
  // still registers over anything with its own click handler underneath.
  useEffect(() => {
    if (!revealed) return;
    const onTap = () => { if (rectRef.current) onDismiss?.(); };
    document.addEventListener("click", onTap, true);
    return () => document.removeEventListener("click", onTap, true);
  }, [revealed, onDismiss]);

  if (!revealed || !rect) return null;

  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const spotTop = rect.top - PAD;
  const spotLeft = rect.left - PAD;
  const spotW = rect.width + PAD * 2;
  const spotH = rect.height + PAD * 2;
  const spotBottom = spotTop + spotH;

  // Simple two-way placement (below, else above) — unlike CoachMarkTour's
  // targets (buttons, toggles), these are whole cards that are always
  // reasonably tall but never so tall they need a side placement too.
  const roomBelow = viewportH - NAV_SAFE_PX - spotBottom - TIP_MARGIN;
  const placement = roomBelow >= 100 ? "below" : "above";
  const tipW = Math.min(340, viewportW - TIP_MARGIN * 2);
  const rawLeft = spotLeft + spotW / 2 - tipW / 2;
  const tipLeft = Math.max(TIP_MARGIN, Math.min(rawLeft, viewportW - TIP_MARGIN - tipW));
  const tipPos = placement === "below"
    ? { top: Math.max(16, spotBottom + TIP_MARGIN), left: tipLeft, width: tipW }
    : { bottom: Math.max(16, viewportH - spotTop + TIP_MARGIN), left: tipLeft, width: tipW };

  return createPortal(
    <>
      <style>{ANIM_CSS}</style>
      <div data-clarity-tour-chrome style={{ position: "fixed", inset: 0, zIndex: Z_BLOCK, background: "transparent" }} />
      <div
        data-clarity-tour-chrome
        className="clarity-coach-spot"
        style={{
          position: "fixed", top: spotTop, left: spotLeft, width: spotW, height: spotH,
          borderRadius: 18, boxShadow: "0 0 0 9999px rgba(35,20,35,0.68)", pointerEvents: "none", zIndex: Z_SPOT,
          transition: "top 0.3s ease, left 0.3s ease, width 0.3s ease, height 0.3s ease",
        }}
      />
      <div
        data-clarity-tour-chrome
        className="clarity-coach-tip"
        style={{
          position: "fixed", zIndex: Z_TIP, pointerEvents: "none",
          background: "var(--c-bg-card, #fff)", borderRadius: 18, boxShadow: "0 10px 32px rgba(0,0,0,0.28)",
          fontFamily: "'Quicksand', sans-serif", padding: "18px 20px",
          transition: "top 0.3s ease, bottom 0.3s ease, left 0.3s ease",
          ...tipPos,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--c-text-accent, #333)", marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13.5, color: "var(--c-text-mid, #555)", lineHeight: 1.45 }}>{body}</div>
      </div>
    </>,
    document.body
  );
}

export default ScrollCoachMark;
